// src/components/doctor/toothchart/ToothChart.tsx
import React, { useRef, useCallback, useState, useEffect } from "react";
import { useClinicStore } from "../../../store/clinicStore";
import { toothGlow, pulse } from "../../../animations/variants";
import { shouldReduceMotion } from "../../../utils/animationSettings";
import "./ToothChart.css";
import { useToothAnimation } from "./useToothAnimation";
import type { ToothEvent } from "../../../types/domain";
import { resolveToothImageKey } from "../../../utils/toothVisualResolver";
import { canonicalToDoctorPngState } from "../../../utils/toothVisualMapping";
import { useEncounterReplay } from "../replay/useEncounterReplay";
import ApexoRadialMenu, { ToothSurface } from "../../common/apexo/ApexoRadialMenu";

/* ISO Notation — 2 rows: upper 16, lower 16 (32 teeth as single image) */
const UPPER = [
  "UR8", "UR7", "UR6", "UR5", "UR4", "UR3", "UR2", "UR1",
  "UL1", "UL2", "UL3", "UL4", "UL5", "UL6", "UL7", "UL8"
];

const LOWER = [
  "LR8", "LR7", "LR6", "LR5", "LR4", "LR3", "LR2", "LR1",
  "LL1", "LL2", "LL3", "LL4", "LL5", "LL6", "LL7", "LL8"
];

function getToothImageSrc(toothId: string, state: string): string {
  const normalized = canonicalToDoctorPngState(state);
  switch (normalized) {
    case "Normal":
      return `/assets/teeth/full/${toothId}.png`;
    case "Filling":
      return `/assets/teeth/Filling/Filling_${toothId}.png`;
    case "RCT_Open":
      return `/assets/teeth/RCT_Open/RO_${toothId}.png`;
    case "RCT_Done":
      return `/assets/teeth/RCT_Done/RC_${toothId}.png`;
    case "Crown":
      return `/assets/teeth/Crown/Crown_${toothId}.png`;
    case "Crown_Cutting":
      return `/assets/teeth/crown_cutting/CC_${toothId}.png`;
    case "RCT_CC":
      return `/assets/teeth/RCT_CC/RCT_CC_${toothId}.png`;
    case "RCT_Crown":
      return `/assets/teeth/RCT_Crown/RCT_CR_${toothId}.png`;
    case "Missing":
      return `/assets/teeth/extraction/Ext_${toothId}.png`;
    default:
      return `/assets/teeth/full/${toothId}.png`;
  }
}

const FIT_X = 870;
const FIT_Y = 0;
const FIT_S = 0.3;
/** Approximate content size at scale 1 (16 teeth × ~52px, 2 rows) */
const CONTENT_WIDTH = 900;
const CONTENT_HEIGHT = 400;

export interface ToothChartProps {
  toothStates?: Record<string, string>;
  surfacesMap?: Record<string, unknown>;
  flagsMap?: Record<string, unknown>;
  movementsMap?: Record<string, unknown>;
  selectedTeeth?: string[];
  onToothClick?: (toothId: string) => void;
  readOnly?: boolean;
  autoFit?: boolean;
  allowUpscale?: boolean;
  scaleOverride?: number | null;
  toothEvents?: ToothEvent[] | null;
  unsupportedLabelsByTooth?: Record<string, string[]>;
  animateStateTransitions?: boolean;
  mutatedTooth?: string | null;
  fitX?: number;
  fitY?: number;
  fitS?: number;
}

export default function ToothChart({
  toothStates = {},
  surfacesMap = {},
  flagsMap = {},
  movementsMap = {},
  selectedTeeth = [],
  onToothClick,
  readOnly = false,
  autoFit = true,
  allowUpscale = false,
  scaleOverride = null,
  toothEvents = null,
  unsupportedLabelsByTooth = {},
  animateStateTransitions = false,
  mutatedTooth = null,
  fitX = FIT_X,
  fitY = FIT_Y,
  fitS = FIT_S,
}: ToothChartProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [computedScale, setComputedScale] = useState<number | null>(null);
  const animatedToothId = useToothAnimation();

  useEffect(() => {
    if (!autoFit || scaleOverride != null) return;
    const el = stageRef.current;
    if (!el) return;
    const updateScale = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w <= 0 || h <= 0) return;
      const sx = w / CONTENT_WIDTH;
      const sy = h / CONTENT_HEIGHT;
      let s = Math.min(sx, sy);
      if (!allowUpscale) s = Math.min(s, FIT_S * 1.5);
      setComputedScale(Math.max(0.2, Math.min(1.2, s)));
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [autoFit, allowUpscale, scaleOverride]);
  const setSelectedTooth = useClinicStore((s) => s.setSelectedTooth);
  const { replayTime, chartState } = useEncounterReplay(
    (Array.isArray(toothEvents) ? toothEvents : []) as ToothEvent[]
  );

  const [radialMenu, setRadialMenu] = useState<{ toothId: string; x: number; y: number } | null>(null);

  const handleClick = useCallback((toothId: string, e: React.MouseEvent) => {
    if (readOnly) return;
    onToothClick?.(toothId);
    setSelectedTooth(toothId);
    
    // Open radial menu at click coordinates
    setRadialMenu({ toothId, x: e.clientX, y: e.clientY });
  }, [readOnly, onToothClick, setSelectedTooth]);

  const handleToggleSurface = (surface: ToothSurface) => {
    // In a full implementation, this would fire an event to the ClinicalWorkspace to update the surface selection
    // For now, it visually acts as a surface selector toggle
    console.log(`Toggled surface ${surface} on tooth ${radialMenu?.toothId}`);
  };

  const renderTooth = (toothId: string) => {
    let currentState = toothStates[toothId] || "Normal";
    // Map resolver output (imageKey) to normalizeState format (mixed case)
    const stateMap: Record<string, string> = {
      normal: "Normal",
      filling: "Filling",
      rct_open: "RCT_Open",
      rct_done: "RCT_Done",
      rct_crown: "RCT_Crown",
      rct_cc: "RCT_CC",
      crown: "Crown",
      crown_cutting: "Crown_Cutting",
      missing: "Missing",
      implant: "Normal", // Implant not in normalizeState, fallback to Normal
    };

    // Replay override: when replayTime is set, prefer encounter replay chartState imageKey.
    // When replayTime is null, keep existing behavior (live resolver / toothStates).
    if (replayTime !== null) {
      const replayKey = chartState?.[toothId];
      currentState = (replayKey && stateMap[replayKey]) || currentState;
    } else if (toothEvents && Array.isArray(toothEvents) && toothEvents.length > 0) {
      const resolvedKey = resolveToothImageKey(toothId, toothEvents as ToothEvent[]);
      currentState = stateMap[resolvedKey] || currentState;
    }
    const imageKey = getToothImageSrc(toothId, currentState);
    const isSelected = selectedTeeth.includes(toothId);
    const isMissing = currentState === "Missing";
    const isAnimating = animatedToothId === toothId;
    const unsupportedLabels = unsupportedLabelsByTooth[toothId] || [];
    const unsupportedCount = unsupportedLabels.length;
    const titleText =
      unsupportedCount > 0
        ? `${toothId} | Extra mapped notes: ${unsupportedLabels.join(", ")}`
        : toothId;

    return (
      <div
        key={toothId}
        className={[
          "tooth-cell",
          isSelected ? "selected" : "",
          isMissing ? "is-missing" : "",
          readOnly ? "readonly" : "",
          isAnimating ? "anim-pulse" : ""
        ].join(" ")}
        onClick={(e) => handleClick(toothId, e)}
        title={titleText}
        style={{ cursor: readOnly ? "default" : "pointer" }}
      >
        <div className="tooth-img-wrapper">
          {animateStateTransitions && !shouldReduceMotion ? (
            <img
              key={`${toothId}-${imageKey}`}
              src={imageKey}
              alt={toothId}
              className="tooth-img tooth-image fade-transition"
              draggable={false}
              loading="lazy"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                const img = e.currentTarget;
                const src = img.src;
                if (src && !src.includes("/full/")) {
                  console.warn("Missing tooth image:", src);
                  img.src = `/assets/teeth/full/${toothId}.png`;
                }
              }}
            />
          ) : (
            <img
              key={imageKey}
              src={imageKey}
              alt={toothId}
              className={`tooth-img tooth-image fade-transition ${isSelected ? 'selected-pulse' : ''} ${toothId === mutatedTooth ? 'mutated-pulse' : ''}`}
              draggable={false}
              loading="lazy"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                const img = e.currentTarget;
                const src = img.src;
                if (src && !src.includes("/full/")) {
                  console.warn("Missing tooth image:", src);
                  img.src = `/assets/teeth/full/${toothId}.png`;
                }
              }}
            />
          )}
        </div>

        {isSelected && <div className="selection-halo" />}
        {unsupportedCount > 0 && (
          <div className="tooth-indicators" aria-hidden="true">
            <span className="indicator unsupported">+{unsupportedCount}</span>
          </div>
        )}
      </div>
    );
  };

  const effectiveScale = typeof fitS === "number" ? fitS : (parseFloat(String(fitS)) || FIT_S);
  const transformStyle = `translate(${Number(fitX)}px, ${Number(fitY)}px) scale(${effectiveScale})`;

  return (
    <div className="odontogram-stage" ref={stageRef} style={{ cursor: readOnly ? 'default' : 'pointer' }}>
      <div
        className="odontogram-scale-wrapper"
        ref={contentRef}
        style={{
          transform: transformStyle,
          transformOrigin: "top left",
        }}
      >
        {/* Upper row — 16 teeth */}
        <div className="arch-row upper">
          {UPPER.map((toothId) => renderTooth(toothId))}
        </div>
        {/* Lower row — 16 teeth (whole chart scaled as one image to fit window) */}
        <div className="arch-row lower">
          {LOWER.map((toothId) => renderTooth(toothId))}
        </div>
      </div>

      {radialMenu && !readOnly && (
        <ApexoRadialMenu
          x={radialMenu.x}
          y={radialMenu.y}
          toothId={radialMenu.toothId}
          selectedSurfaces={[]} // Would map from chartState.surfaces in a deeper integration
          onToggleSurface={handleToggleSurface}
          onClose={() => setRadialMenu(null)}
        />
      )}
    </div>
  );
}
